import {
  Box,
  Button,
  GridLegacy as Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import {
  FinderFiltersResponse,
  FinderResultsRequest,
  useGetFinderResults,
} from '@/services';
import { FormattedMessage, useIntl } from 'react-intl';

import Head from 'next/head';
import { pxToRem } from '@/theme/typography';
import { useQueryClient } from '@tanstack/react-query';
import { triggerGAEvent } from '@/logic/shared/data-layer-events';
import {
  MovieItemModel,
  ViewItemListModel,
  EventTypes,
} from '@/logic/types/data-layer-models';
import { useCity } from '@/logic/hooks';
import { MovieCollapseList } from '../shared/movie-collapse-list';
import { MovieEmptyState } from '../shared/movie-empty-state';
import { Filters } from './filters';

type ResultsProps = {
  filters: FinderResultsRequest;
  finderFiltersResults: FinderFiltersResponse;
  handleEditFilters: () => void;
};

export const MovieFinderResults: FC<ResultsProps> = ({
  filters,
  finderFiltersResults,
  handleEditFilters,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('tablet'));
  const queryClient = useQueryClient();
  const { cityId, getCity } = useCity();

  const { data, isError, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } =
    useGetFinderResults({
      params: { ...filters, page: 1, limit: 4 },
    });

  const movies = useMemo(
    () => (data?.pages ? data?.pages?.flatMap(group => group.data) : []),
    [data]
  );

  const callOnFilmHasBeenStarted = (): void => {
    // invalidate movies
    queryClient.invalidateQueries([{ url: 'film-finder' }]);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const triggerDataLayerEvent = (movies: MovieItemModel[]): void => {
    const data: ViewItemListModel = {
      event: EventTypes.VIEW_ITEM_LIST,
      ecommerce: {
        item_list_name: 'Movie Finder', // the category of listed movies eg: search
        items: movies,
      },
    };
    triggerGAEvent(data, true);
  };

  useEffect(() => {
    const moviesList = movies;
    if (!moviesList || moviesList.length === 0) return;
    const _movies: MovieItemModel[] = moviesList.map(
      ({ id, title, genres, cinemas, experiances }) => {
        return {
          item_id: id.toString(),
          item_name: title,
          item_brand: genres.map(genre => genre.name).join(','),
          item_category: experiances.map(experiance => experiance.name).join(','),
          item_category2: '',
          item_list_id: 'Movie Finder',
          item_list_name: 'Movie Finder',
          location: getCity(cityId)?.name,
          mall: cinemas.map(cinema => cinema.name).join(','),
          price: 0,
          quantity: 1,
        };
      }
    );
    triggerDataLayerEvent(_movies);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movies.length]);

  return (
    <>
      <Head>
        <title>
          {intl.formatMessage({ id: 'movie.finder' }) +
            ' | ' +
            intl.formatMessage({ id: 'global.results' })}
        </title>
      </Head>
      <Box
        sx={{
          marginBlock: {
            mobile: '32px',
            tablet: pxToRem(52),
            position: 'relative',
          },
        }}
      >
        <Box
          sx={{
            marginInline: {
              mobile: '16px',
              tablet: pxToRem(128),
            },
          }}
        >
          <Stack
            direction='row'
            justifyContent={'space-between'}
            alignItems='center'
            sx={{
              marginBottom: {
                mobile: '24px',
                tablet: pxToRem(52),
              },
            }}
          >
            <Typography
              sx={{
                color: theme.palette.grey[80],
                fontSize: pxToRem(30),
                fontWeight: 700,
                lineHeight: pxToRem(24),
              }}
            >
              <FormattedMessage id='movie_finder.search_result' />
            </Typography>

            <Button
              onClick={handleEditFilters}
              color='primary'
              variant={isTablet ? 'contained' : 'text'}
              sx={{
                paddingX: '20px',
                fontSize: {
                  mobile: '14px',
                  tablet: pxToRem(16),
                },
                fontFamily: theme.palette.typography.font.bold,
                fontWeight: 700,
                color: {
                  mobile: theme.palette.grey[50],
                  tablet: 'white',
                },
              }}
            >
              <FormattedMessage id='movie_finder.edit_filters' />
            </Button>
          </Stack>
          <Filters finderFiltersResults={finderFiltersResults} />
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginTop: {
                mobile: '24px',
                tablet: pxToRem(56),
              },
            }}
          >
            <Grid item mobile={12} tablet={6}>
              {!isError && (
                <MovieCollapseList
                  movies={movies}
                  hasMore={hasNextPage}
                  fetchMovies={fetchNextPage}
                  isFetching={isLoading}
                  isFetchingNextPage={isFetchingNextPage}
                  showExperiencesBy='cinema'
                  filters={filters}
                  showLoader={true}
                  callOnFilmHasBeenStarted={callOnFilmHasBeenStarted}
                />
              )}

              {isError && (
                <MovieEmptyState
                  title='global.error'
                  imgWidth={{ tablet: pxToRem(500) }}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};
